import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as luxon from 'luxon';
import {
  navigateToCommunity,
  navigateToMember,
  navigateToSite,
} from '@flexo/resolvers';
import { resolveCommunityForMember, resolveCommunityForSite, resolveContextCommunity } from '@flexo/general';
import { getOrgRole, isSupervisor, ORG_ROLES } from '@flexo/hooks';
import { BreadcrumbItem, HeaderType } from '../types';
import { useEntityData } from './useEntityData';

export const useBreadcrumbs = (type: HeaderType): BreadcrumbItem[] => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const calendar = useSelector((state: any) => state.calendar);
  const user = useSelector((state: any) => state.user);
  // FLEXO-6114: ephemeral community-of-origin + visible sites (pcc-clean proxy).
  const navContextCommunityId = useSelector((state: any) => state.metadata?.navContextCommunityId);
  const navContextMemberId = useSelector((state: any) => state.metadata?.navContextMemberId);
  const entitySites = useSelector((state: any) => state.metadata?.entities?.site);
  const { getCommunityById, getSiteById, getMemberById, getAllCommunities, getAllMembers } =
    useEntityData();
  const queryParams = window?.location?.search || '';

  // FLEXO-6066: end users navigate a single community as themselves, so the
  // breadcrumb omits the redundant "Comunità" list link and their own name,
  // and surfaces the community selector directly.
  const isEndUserBreadcrumb = getOrgRole(user) === ORG_ROLES.END_USER;

  const dateFrom = calendar?.hiveCalendar?.selectedDate?.[0]
    ? luxon.DateTime.fromJSDate(calendar.hiveCalendar.selectedDate[0]).toFormat('yyyy-MM-dd')
    : luxon.DateTime.now().toFormat('yyyy-MM-dd');

  return useMemo(() => {

    // FLEXO-6076 #1/#2 + FLEXO-6077 #6: end users get a single combined
    // site+community entity selector (EndUserEntityBreadcrumb) instead of the
    // static breadcrumb chain. Centralised here so the header renders it via the
    // normal breadcrumb type switch (no ad-hoc conditional in the header JSX).
    if (isEndUserBreadcrumb) {
      return [{ type: 'enduser-entity', isClickable: false }];
    }

    // FLEXO-4604 (web only): a supervisor with a single community must not see
    // the "Comunità" list link in the breadcrumb trail — like the end-user
    // case, the trail surfaces the community directly.
    const isSingleCommunitySupervisor =
      isSupervisor(getOrgRole(user)) && getAllCommunities().length === 1;

    // FLEXO-6114: visible sites = pcc-clean proxy (sites the backend SiteFilter
    // kept in the payload). Changelog entries for filtered (pcc/pccv) sites are
    // ignored when resolving the community context.
    const visibleSiteIds = new Set<string>(
      (entitySites || []).map((s: any) => s?.siteId).filter(Boolean)
    );

    const member = getMemberById(params.id as string);
    const memberCommunityId =
      resolveContextCommunity(member, {
        fromCommunity: navContextCommunityId,
        dateFrom,
        visibleSiteIds,
      }) || member?.communityId;
    const community = getCommunityById(memberCommunityId) || getAllCommunities()[0];

    switch (type) {
      case 'headerWebAdmin':
        return [
          {
            label: t('widgets.header.communities'),
            onClick: () => navigate(`/communities`),
            isClickable: true,
          },
          {
            label: t('widgets.header.admin_users'),
            isClickable: false,
          },
        ];
      case 'communities':
        if (isEndUserBreadcrumb) {
          return [
            {
              type: 'communities-dropdown',
              isClickable: false,
            },
          ];
        }

        return [
          {
            label: t('widgets.header.communities'),
            isClickable: false,
          },
        ];

      case 'community':
        if (isEndUserBreadcrumb || isSingleCommunitySupervisor) {
          // End user / single-community supervisor (FLEXO-4604): no "Comunità"
          // list link — show the community selector directly.
          return [
            {
              type: 'communities-dropdown',
              isClickable: false,
            },
          ];
        }

        return [
          {
            label: t('widgets.header.communities'),
            onClick: () => navigate(`/communities${queryParams}`),
            isClickable: true,
          },
          {
            type: 'communities-dropdown',
            isClickable: false,
          },
        ];

      case 'member': {
        const member = getMemberById(params.id as string);
        const memberCommunityId = resolveContextCommunity(member, {
          fromCommunity: navContextCommunityId,
          dateFrom,
          visibleSiteIds,
        }) || member?.communityId;
        const community =
          getCommunityById(memberCommunityId) || getAllCommunities()[0];

        if (isEndUserBreadcrumb) {
          // End user: /member is the aggregate "all sites" view, so surface the
          // sites selector instead of the community/member breadcrumb chain.
          return [
            {
              type: 'sites-dropdown',
              isClickable: false,
            },
          ];
        }

        return [
          // FLEXO-4604: single-community supervisor drops the "Comunità" list link.
          ...(isSingleCommunitySupervisor
            ? []
            : [
                {
                  label: t('widgets.header.communities'),
                  onClick: () => navigate(`/communities${queryParams}`),
                  isClickable: true,
                },
              ]),
          {
            label: community?.communityName || 'Community name not found',
            title: community?.communityCode,
            onClick: () =>
              navigateToCommunity('community', community?.communityId),
            isClickable: true,
          },
          {
            type: 'members-dropdown',
            isClickable: false,
          },
        ];
      }

      case 'member-data-vis': {
        const member = getMemberById(params.id as string);
        const memberCommunityId = resolveContextCommunity(member, {
          fromCommunity: navContextCommunityId,
          dateFrom,
          visibleSiteIds,
        }) || member?.communityId;
        const community =
          getCommunityById(memberCommunityId) || getAllCommunities()[0];
        const memberName =
          !member?.memberFirstName && !member?.memberLastName
          ? 'Member name not found'
          : `${member?.memberFirstName || ''} ${member?.memberLastName || ''}`.trim();

        return [
          {
            label: community?.communityName || 'Community name not found',
            title: community?.communityCode,
            onClick: () =>
              navigateToCommunity('community', community?.communityId),
            isClickable: true,
          },
          // End user: omit their own name — this is their own aggregate dashboard.
          ...(isEndUserBreadcrumb
            ? []
            : [
                {
                  label: memberName,
                  title: member?.memberExternalId,
                  onClick: () => navigateToMember('member', params.id as string),
                  isClickable: true,
                },
              ]),
        ];
      }

      case 'site': {
        const site = getSiteById(params.id as string);
        // End-user: the site's primary `memberId` may belong to a different
        // member than the logged-in one (shared app account), so it won't be in
        // this user's entities. Fall back to the member whose changelog contains
        // this site — the member who actually owns it for this user.
        const member =
          // FLEXO-6114: prefer the member-of-origin (site opened from a member
          // page) so the breadcrumb keeps that member, not the site's arbitrary
          // primary `memberId` (which can be a shared/other member).
          (navContextMemberId ? getMemberById(navContextMemberId) : null) ||
          getMemberById(site?.memberId as string) ||
          getAllMembers().find((m: any) =>
            ((m?.changelog as any[]) || []).some(
              (c: any) => c?.siteId === site?.siteId
            )
          );
        const resolvedCommunityId =
          resolveCommunityForSite(site, dateFrom) ||
          resolveCommunityForMember(member, dateFrom) ||
          member?.communityId || site?.communityId;
        const community =
          getCommunityById(resolvedCommunityId) || getAllCommunities()[0];
        const siteName =
        site?.siteName || site?.pod || site?.name ||  'Site name not found';

        return [
          {
            label:
              `${member?.memberFirstName || ''} ${
                member?.memberLastName || ''
              }`.trim() || 'Member name not found',
            title: member?.memberExternalId,
            onClick: () => navigateToMember('member', member?.memberId),
            isClickable: true,
          },
          {
            label: siteName,
            title: site?.pod,
            onClick: () => navigateToSite('site', params.id as string),
            isClickable: true,
          },
        ];
      }

      case 'site-overview': {
        const site = getSiteById(params.id as string);
        // End-user: site's primary `memberId` may differ from the logged-in
        // member; fall back to the member whose changelog contains this site.
        const member =
          // FLEXO-6114: prefer the member-of-origin (site opened from a member
          // page) so the breadcrumb keeps that member, not the site's arbitrary
          // primary `memberId` (which can be a shared/other member).
          (navContextMemberId ? getMemberById(navContextMemberId) : null) ||
          getMemberById(site?.memberId as string) ||
          getAllMembers().find((m: any) =>
            ((m?.changelog as any[]) || []).some(
              (c: any) => c?.siteId === site?.siteId
            )
          );
        const resolvedCommunityId =
          resolveCommunityForSite(site, dateFrom) ||
          resolveCommunityForMember(member, dateFrom) ||
          member?.communityId || site?.communityId;
        const community =
          getCommunityById(resolvedCommunityId) || getAllCommunities()[0];

        return [
          {
            label: community?.communityName || 'Community name not found',
            title: community?.communityCode,
            onClick: () =>
              navigateToCommunity('community', community?.communityId),
            isClickable: true,
          },
          // End user: omit their own name — they are the site owner, no need to
          // restate who they are.
          ...(isEndUserBreadcrumb
            ? []
            : [
                {
                  label:
                    `${member?.memberFirstName || ''} ${
                      member?.memberLastName || ''
                    }`.trim() || 'Member name not found',
                  title: member?.memberExternalId,
                  onClick: () => navigateToMember('member', member?.memberId),
                  isClickable: true,
                },
              ]),
          {
            type: 'sites-dropdown',
            isClickable: false,
          },
        ];
      }

      case 'community-data-vis': {
        const communities = getAllCommunities();
        const community =
          communities.find(
            (c: any) => (c?.communityId || c?.flexoCommunityId) === params.id
          ) || communities[0];

        if (isEndUserBreadcrumb) {
          // End user: drop "Comunità > <name>" and surface the community selector.
          return [
            {
              type: 'communities-dropdown',
              isClickable: false,
            },
          ];
        }

        if (isSingleCommunitySupervisor) {
          // FLEXO-4604 review: with a single community there is nothing to
          // select, so render the community name as a clickable root crumb
          // back to the community overview (the dropdown would be disabled,
          // unclickable and unellipsized, overflowing under the logo).
          return [
            {
              label: community?.communityName || 'Community name not found',
              title: community?.communityCode,
              onClick: () =>
                navigateToCommunity('community', params.id as string),
              isClickable: true,
            },
          ];
        }

        return [
          {
            label: t('widgets.header.communities'),
            onClick: () => navigate(`/communities${queryParams}`),
            isClickable: true,
          },
          {
            label: community?.communityName || 'Community name not found',
            title: community?.communityCode,
            onClick: () =>
              navigateToCommunity('community', params.id as string),
            isClickable: true,
          },
        ];
      }

      default:
        return [];
    }
  }, [
    type,
    params,
    getCommunityById,
    getSiteById,
    getMemberById,
    getAllCommunities,
    t,
    navigate,
    queryParams,
    dateFrom,
    isEndUserBreadcrumb,
    user,
    navContextCommunityId,
    navContextMemberId,
    entitySites,
  ]);
};
